import { Pane } from 'tweakpane';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type { AnimationState } from '@game/SpineAnimationController';

type DebugPaneState = {
	x: number;
	y: number;
	open: boolean;
};

const STORAGE_KEY = 'superjump.debugPane';
const STORAGE_TUNING_KEY = 'superjump.tuning';
const STORAGE_TWEAKPANE_STATE_KEY = 'superjump.tweakpaneState';

function safeParse<T>(raw: string | null): T | null {
	if (!raw) return null;
	try {
		return JSON.parse(raw) as T;
	} catch {
		return null;
	}
}

function loadPaneState(): DebugPaneState {
	const state = safeParse<DebugPaneState>(localStorage.getItem(STORAGE_KEY));
	return state ?? { x: 12, y: 12, open: true };
}

function savePaneState(state: DebugPaneState) {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function loadTuning(): Tuning {
	const saved = safeParse<Partial<Tuning>>(localStorage.getItem(STORAGE_TUNING_KEY));
	return { ...DEFAULT_TUNING, ...(saved ?? {}) };
}

function saveTuning(tuning: Tuning) {
	localStorage.setItem(STORAGE_TUNING_KEY, JSON.stringify(tuning));
}

export type SuperJumpDebugPaneOptions = {
	initialTuning?: Tuning;
	onChange: (patch: Partial<Tuning>) => void;
	onEnemiesFrozenChange?: (frozen: boolean) => void;
	onPlayerAnimationOverride?: (state: AnimationState | null) => void;
	onRestartPlayerAnimationOverride?: () => void;
};

export type SuperJumpDebugPaneHandle = {
	dispose: () => void;
	getTuning: () => Tuning;
};

export function initSuperJumpDebugPane(options: SuperJumpDebugPaneOptions): SuperJumpDebugPaneHandle {
	if (typeof window === 'undefined') {
		return {
			dispose: () => undefined,
			getTuning: () => options.initialTuning ?? DEFAULT_TUNING
		};
	}

	const savedTuning = loadTuning();
	// Saved tuning should win over provided initial tuning (so reload works predictably)
	const params: Tuning = { ...DEFAULT_TUNING, ...(options.initialTuning ?? {}), ...savedTuning };

	const wrapper = document.createElement('div');
	const savedState = loadPaneState();
	wrapper.style.position = 'absolute';
	wrapper.style.top = `${savedState.y}px`;
	wrapper.style.left = `${savedState.x}px`;
	wrapper.style.zIndex = '9999';
	wrapper.style.pointerEvents = 'auto';

	const handle = document.createElement('div');
	handle.style.position = 'absolute';
	handle.style.top = '3px';
	handle.style.right = '3px';
	handle.style.height = '32px';
	handle.style.width = '32px';
	handle.style.borderRadius = '12px';
	handle.style.cursor = 'move';
	handle.style.pointerEvents = 'auto';
	handle.style.zIndex = '2';
	handle.style.touchAction = 'none';

	const paneHost = document.createElement('div');
	paneHost.style.position = 'relative';
	paneHost.style.pointerEvents = 'auto';

	wrapper.appendChild(handle);
	wrapper.appendChild(paneHost);
	document.body.appendChild(wrapper);

	let isOpen = savedState.open;

	const toggleButton = document.createElement('button');
	toggleButton.textContent = isOpen ? '×' : '+';
	toggleButton.style.position = 'absolute';
	toggleButton.style.top = '0px';
	toggleButton.style.left = '0px';
	toggleButton.style.width = '24px';
	toggleButton.style.height = '24px';
	toggleButton.style.border = 'none';
	toggleButton.style.borderRadius = '50%';
	toggleButton.style.cursor = 'pointer';
	toggleButton.style.fontWeight = 'bold';
	toggleButton.style.background = 'transparent';
	toggleButton.style.color = 'rgba(0,0,0,0.55)';
	toggleButton.style.zIndex = '3';
	toggleButton.addEventListener('click', (e) => {
		e.stopPropagation();
		isOpen = !isOpen;
		paneHost.style.display = isOpen ? 'block' : 'none';
		toggleButton.textContent = isOpen ? '×' : '+';
		const rect = wrapper.getBoundingClientRect();
		savePaneState({ x: rect.left, y: rect.top, open: isOpen });
	});
	wrapper.appendChild(toggleButton);

	const pane = new Pane({
		title: 'Super Jump Debug',
		container: paneHost
	});

	const stats = { fps: 0 };
	let fpsRafId: number | null = null;
	let lastFrameTime = performance.now();
	const updateFps = (time: number) => {
		const delta = time - lastFrameTime;
		lastFrameTime = time;
		stats.fps = delta > 0 ? Math.min(240, 1000 / delta) : 0;
		fpsRafId = window.requestAnimationFrame(updateFps);
	};
	fpsRafId = window.requestAnimationFrame(updateFps);

	const saveUiState = () => {
		const exportState = (pane as any).exportState?.();
		if (exportState) {
			localStorage.setItem(STORAGE_TWEAKPANE_STATE_KEY, JSON.stringify(exportState));
		}
	};

	const applyAll = () => {
		options.onChange({ ...params });
		saveTuning(params);
		saveUiState();
	};

	const applyOne = <K extends keyof Tuning>(key: K, value: Tuning[K]) => {
		(params as any)[key] = value;
		options.onChange({ [key]: value } as Partial<Tuning>);
		saveTuning(params);
		saveUiState();
	};

	const add = <K extends keyof Tuning>(folder: any, key: K, bindingOptions?: any) => {
		const binding = folder.addBinding(params, key as any, bindingOptions ?? {});
		binding.on?.('change', (ev: { value: Tuning[K] }) => applyOne(key, ev.value));
		return binding;
	};

	const bindAll = (folder: any) => {
		folder.addBinding(stats, 'fps', { label: 'FPS', readonly: true, format: (v: number) => `${v.toFixed(0)}` });

		const debugActions = folder.addFolder({ title: 'Debug Actions' });
		const debugState: {
			enemiesFrozen: boolean;
			playerAnim: 'auto' | AnimationState;
		} = {
			enemiesFrozen: false,
			playerAnim: 'auto'
		};

		debugActions
			.addBinding(debugState, 'enemiesFrozen', { label: 'Freeze enemies' })
			.on?.('change', (ev: { value: boolean }) => options.onEnemiesFrozenChange?.(ev.value));

		debugActions.addBinding(debugState, 'playerAnim', {
			label: 'Player anim',
			options: {
				Auto: 'auto',
				Idle: 'idle',
				Run: 'run',
				Jump: 'jump',
				Walk: 'walk',
				Aim: 'aim',
				Shoot: 'shoot',
				Death: 'death'
			}
		}).on?.('change', (ev: { value: 'auto' | AnimationState }) => {
			if (ev.value === 'auto') {
				options.onPlayerAnimationOverride?.(null);
				return;
			}
			options.onPlayerAnimationOverride?.(ev.value);
		});

		debugActions.addButton({ title: 'Restart anim' }).on('click', () => {
			options.onRestartPlayerAnimationOverride?.();
		});

		const input = folder.addFolder({ title: 'Input & Time' });
		add(input, 'inputDeadzone', { min: 0, max: 0.6, step: 0.01 });
		add(input, 'slowmoMin', { min: 0.05, max: 1, step: 0.01 });
		add(input, 'slowmoMax', { min: 0.2, max: 2, step: 0.01 });
		add(input, 'slowmoDownMultiplier', { min: 0.1, max: 1, step: 0.01 });

		const player = folder.addFolder({ title: 'Player' });
		add(player, 'playerSize', { min: 20, max: 96, step: 1 });
		add(player, 'playerSpeed', { min: 60, max: 600, step: 5 });
		add(player, 'gravity', { min: 100, max: 2200, step: 10 });
		add(player, 'moveMinScale', { min: 0.1, max: 1, step: 0.01 });
		add(player, 'playerMaxHealth', { min: 1, max: 20, step: 1 });

		const jump = folder.addFolder({ title: 'Jump' });
		add(jump, 'jumpForce', { min: 100, max: 1600, step: 10 });
		add(jump, 'jumpInputThreshold', { min: -1, max: -0.05, step: 0.01 });
		add(jump, 'jumpMagThreshold', { min: 0, max: 1, step: 0.01 });
		add(jump, 'jumpInitialScale', { min: 0.05, max: 1, step: 0.01 });
		add(jump, 'jumpHoldForce', { min: 0, max: 2400, step: 10 });
		add(jump, 'jumpChargeMin', { min: 0, max: 0.5, step: 0.01 });
		add(jump, 'jumpChargeMax', { min: 0.05, max: 1.5, step: 0.01 });

		const dash = jump.addFolder({ title: 'Dash' });
		add(dash, 'dashEnabled', {});
		add(dash, 'dashSpeed', { min: 100, max: 2400, step: 20 });
		add(dash, 'dashDuration', { min: 0.03, max: 0.4, step: 0.01 });
		add(dash, 'dashCooldown', { min: 0.05, max: 4, step: 0.05 });
		add(dash, 'dashSwipeMinDistancePx', { min: 10, max: 260, step: 5 });
		add(dash, 'dashSwipeMaxTime', { min: 0.05, max: 0.6, step: 0.01 });

		const downDrop = jump.addFolder({ title: 'Down Drop' });
		add(downDrop, 'downDropEnabled', {});
		add(downDrop, 'downDropInputThreshold', { min: 0, max: 1, step: 0.01 });
		add(downDrop, 'downDropTimeToPlatform', { min: 0.05, max: 0.8, step: 0.01 });
		add(downDrop, 'downDropMaxSpeed', { min: 200, max: 4000, step: 20 });
		add(downDrop, 'downDropGravityScale', { min: 1, max: 10, step: 0.1 });

		const platformDrop = jump.addFolder({ title: 'Platform Drop' });
		add(platformDrop, 'platformDropEnabled', {});
		add(platformDrop, 'platformDropInputThreshold', { min: 0, max: 1, step: 0.01 });
		add(platformDrop, 'platformDropHoldDuration', { min: 0, max: 0.6, step: 0.01 });
		add(platformDrop, 'platformDropIgnoreDuration', { min: 0.02, max: 0.6, step: 0.01 });
		add(platformDrop, 'platformDropKickSpeed', { min: 0, max: 1200, step: 10 });

		const ledge = folder.addFolder({ title: 'Ledge' });
		add(ledge, 'ledgeGrabEnabled', {});
		add(ledge, 'ledgeGrabMargin', { min: 0, max: 120, step: 1 });
		add(ledge, 'ledgeGrabMaxUpSpeed', { min: 0, max: 800, step: 5 });
		add(ledge, 'ledgePullSpeed', { min: 20, max: 600, step: 5 });
		add(ledge, 'ledgeJumpLock', { min: 0, max: 1, step: 0.01 });
		add(ledge, 'timeBoostLedgeValue', { min: 0.1, max: 2, step: 0.01 });
		add(ledge, 'timeBoostLedgeDuration', { min: 0.01, max: 1, step: 0.01 });

		const wall = folder.addFolder({ title: 'Wall Run' });
		add(wall, 'wallRunEnabled', {});
		add(wall, 'wallRunMargin', { min: 0, max: 40, step: 1 });
		add(wall, 'wallRunMaxTime', { min: 0.1, max: 4, step: 0.05 });
		add(wall, 'wallRunMaxAscent', { min: 20, max: 800, step: 5 });
		add(wall, 'wallRunRegrabLock', { min: 0, max: 1, step: 0.01 });
		add(wall, 'wallRunInputXMin', { min: 0, max: 1, step: 0.01 });
		add(wall, 'wallRunUpInputThreshold', { min: -1, max: 0, step: 0.01 });
		add(wall, 'wallRunGravityScale', { min: 0, max: 1, step: 0.01 });
		add(wall, 'wallRunMaxFallSpeed', { min: 0, max: 600, step: 5 });
		add(wall, 'wallRunUpAccel', { min: 0, max: 2400, step: 10 });
		add(wall, 'wallJumpInputAwayMin', { min: 0, max: 1, step: 0.01 });
		add(wall, 'wallJumpXSpeed', { min: 0, max: 1000, step: 10 });
		add(wall, 'wallJumpYScale', { min: 0.1, max: 1.4, step: 0.01 });

		const platforms = folder.addFolder({ title: 'Platforms & Camera' });
		add(platforms, 'platformHeight', { min: 1, max: 120, step: 1 });
		add(platforms, 'platformDefaultBottom', { min: 0, max: 600, step: 5 });
		add(platforms, 'platformMinSideOffset', { min: 0, max: 120, step: 1 });
		add(platforms, 'platformWidthRatio', { min: 0.2, max: 1, step: 0.01 });
		add(platforms, 'platformMinWidth', { min: 40, max: 400, step: 5 });
		add(platforms, 'platformMaxWidth', { min: 60, max: 520, step: 5 });
		add(platforms, 'platformGapMin', { min: 40, max: 400, step: 5 });
		add(platforms, 'platformGapMax', { min: 60, max: 600, step: 5 });
		add(platforms, 'platformCleanupBelow', { min: 0, max: 800, step: 10 });
		add(platforms, 'cameraFollowThreshold', { min: 40, max: 520, step: 5 });
		add(platforms, 'cameraDownFollowEnabled', {});
		add(platforms, 'cameraDownFollowThreshold', { min: 40, max: 420, step: 5 });
		add(platforms, 'cameraDownFollowMax', { min: 0, max: 800, step: 10 });
		add(platforms, 'cameraSpawnAhead', { min: 100, max: 1400, step: 10 });

		const destruction = platforms.addFolder({ title: 'Platform Destruction' });
		add(destruction, 'platformDestructionEnabled', {});
		add(destruction, 'platformSplitGap', { min: 0, max: 60, step: 1 });
		add(destruction, 'platformMinPieceWidth', { min: 10, max: 240, step: 1 });
		add(destruction, 'explosionDuration', { min: 0.05, max: 1, step: 0.01 });
		add(destruction, 'explosionRadius', { min: 4, max: 120, step: 1 });

		const enemies = folder.addFolder({ title: 'Enemies' });
		add(enemies, 'enemySpawnAhead', { min: 100, max: 1400, step: 10 });
		add(enemies, 'enemyGapMin', { min: 60, max: 900, step: 10 });
		add(enemies, 'enemyGapMax', { min: 80, max: 1200, step: 10 });
		add(enemies, 'enemyMarginX', { min: 0, max: 200, step: 1 });
		add(enemies, 'enemySize', { min: 10, max: 80, step: 1 });
		add(enemies, 'enemyCooldownMin', { min: 0.1, max: 5, step: 0.05 });
		add(enemies, 'enemyCooldownMax', { min: 0.1, max: 8, step: 0.05 });
		add(enemies, 'enemiesOnlyShootInView', {});

		const enemyAi = enemies.addFolder({ title: 'AI' });
		add(enemyAi, 'enemyMoveEnabled', {});
		add(enemyAi, 'enemyMoveSpeed', { min: 0, max: 600, step: 5 });
		add(enemyAi, 'enemyMoveAccel', { min: 0, max: 4000, step: 10 });
		add(enemyAi, 'enemyWanderStrength', { min: 0, max: 1, step: 0.01 });
		add(enemyAi, 'enemyWanderFreq', { min: 0, max: 4, step: 0.01 });

		add(enemyAi, 'enemyDodgeEnabled', {});
		add(enemyAi, 'enemyDodgeLookahead', { min: 0, max: 2, step: 0.01 });
		add(enemyAi, 'enemyDodgePadding', { min: 0, max: 120, step: 1 });
		add(enemyAi, 'enemyDodgeStrength', { min: 0, max: 4, step: 0.01 });

		add(enemyAi, 'enemyLosEnabled', {});
		add(enemyAi, 'enemyLosMargin', { min: 0, max: 40, step: 1 });
		add(enemyAi, 'enemyLosRepathInterval', { min: 0.01, max: 1, step: 0.01 });
		add(enemyAi, 'enemyLosSearchStep', { min: 4, max: 120, step: 1 });
		add(enemyAi, 'enemyLosSearchMax', { min: 40, max: 800, step: 10 });

		const enemyBurst = enemyAi.addFolder({ title: 'Burst Fire' });
		add(enemyBurst, 'enemyBurstEnabled', {});
		add(enemyBurst, 'enemyBurstCountMin', { min: 1, max: 20, step: 1 });
		add(enemyBurst, 'enemyBurstCountMax', { min: 1, max: 20, step: 1 });
		add(enemyBurst, 'enemyBurstInterval', { min: 0.02, max: 0.6, step: 0.01 });
		add(enemyBurst, 'enemyBurstSpreadDeg', { min: 0, max: 30, step: 1 });

		const proj = folder.addFolder({ title: 'Enemy Projectiles' });
		add(proj, 'projectileSpeed', { min: 50, max: 1200, step: 10 });
		add(proj, 'projectileLifetime', { min: 0.2, max: 12, step: 0.1 });
		add(proj, 'projectileSize', { min: 2, max: 40, step: 1 });
		add(proj, 'projectilePlayerDamage', { min: 0, max: 10, step: 1 });

		const pproj = folder.addFolder({ title: 'Player Projectiles' });
		add(pproj, 'playerProjectileSpeed', { min: 50, max: 1600, step: 10 });
		add(pproj, 'playerProjectileLifetime', { min: 0.05, max: 6, step: 0.05 });
		add(pproj, 'playerProjectileSize', { min: 2, max: 40, step: 1 });

		const ammo = folder.addFolder({ title: 'Ammo & Reload' });
		add(ammo, 'playerReloadDuration', { min: 0.1, max: 6, step: 0.05 });
		add(ammo, 'enemyReloadDuration', { min: 0.1, max: 6, step: 0.05 });

		const melee = folder.addFolder({ title: 'Melee' });
		add(melee, 'meleeRadius', { min: 0, max: 200, step: 1 });
		add(melee, 'meleeCooldown', { min: 0, max: 1, step: 0.01 });

		const boost = folder.addFolder({ title: 'Time Boost (Fire / Melee / Dash / Ledge)' });
		add(boost, 'timeBoostDuration', { min: 0.01, max: 1, step: 0.01 });
		add(boost, 'timeBoostFireValue', { min: 0.1, max: 2, step: 0.01 });
		add(boost, 'timeBoostMeleeValue', { min: 0.1, max: 2, step: 0.01 });
		add(boost, 'timeBoostDashValue', { min: 0.1, max: 2, step: 0.01 });
		add(boost, 'timeBoostDashDuration', { min: 0.01, max: 1, step: 0.01 });
		add(boost, 'timeBoostLedgeValue', { min: 0.1, max: 2, step: 0.01 });
		add(boost, 'timeBoostLedgeDuration', { min: 0.01, max: 1, step: 0.01 });

		folder.addButton({ title: 'Reset defaults' }).on('click', () => {
			Object.assign(params, DEFAULT_TUNING);
			applyAll();
			(pane as any).refresh?.();
		});
	};

	bindAll(pane as any);

	const savedUiState = safeParse<any>(localStorage.getItem(STORAGE_TWEAKPANE_STATE_KEY));
	if (savedUiState) {
		(pane as any).importState?.(savedUiState);
	}

	// Persist UI state (expanded/collapsed folders) even if user only toggles folders.
	let uiSaveTimeout: number | null = null;
	const scheduleSaveUi = () => {
		if (uiSaveTimeout !== null) window.clearTimeout(uiSaveTimeout);
		uiSaveTimeout = window.setTimeout(() => {
			uiSaveTimeout = null;
			saveUiState();
		}, 0);
	};
	paneHost.addEventListener('click', scheduleSaveUi);

	let dragging = false;
	let startX = 0;
	let startY = 0;
	let startLeft = 0;
	let startTop = 0;
	let activePointerId: number | null = null;

	const handlePointerMove = (event: PointerEvent) => {
		if (!dragging) return;
		const dx = event.clientX - startX;
		const dy = event.clientY - startY;
		wrapper.style.left = `${startLeft + dx}px`;
		wrapper.style.top = `${startTop + dy}px`;
		wrapper.style.right = 'auto';
	};

	const handlePointerUp = () => {
		if (!dragging) return;
		dragging = false;
		window.removeEventListener('pointermove', handlePointerMove);
		window.removeEventListener('pointerup', handlePointerUp);
		if (activePointerId !== null) {
			handle.releasePointerCapture?.(activePointerId);
			activePointerId = null;
		}
		const rect = wrapper.getBoundingClientRect();
		savePaneState({ x: rect.left, y: rect.top, open: isOpen });
	};

	const handlePointerDown = (event: PointerEvent) => {
		event.preventDefault();
		event.stopPropagation();
		dragging = true;
		startX = event.clientX;
		startY = event.clientY;
		const rect = wrapper.getBoundingClientRect();
		startLeft = rect.left;
		startTop = rect.top;
		activePointerId = event.pointerId;
		handle.setPointerCapture?.(event.pointerId);
		window.addEventListener('pointermove', handlePointerMove, { passive: false });
		window.addEventListener('pointerup', handlePointerUp, { passive: false });
	};

	handle.addEventListener('pointerdown', handlePointerDown);

	paneHost.style.display = isOpen ? 'block' : 'none';

	const dispose = () => {
		paneHost.removeEventListener('click', scheduleSaveUi);
		handle.removeEventListener('pointerdown', handlePointerDown);
		window.removeEventListener('pointermove', handlePointerMove);
		window.removeEventListener('pointerup', handlePointerUp);
		if (fpsRafId !== null) {
			window.cancelAnimationFrame(fpsRafId);
			fpsRafId = null;
		}
		pane.dispose();
		wrapper.remove();
	};

	// apply + persist immediately (ensure load from localStorage takes effect right away)
	applyAll();

	return {
		dispose,
		getTuning: () => ({ ...params })
	};
}
